import React, { useState, useEffect } from 'react'
import { Info, Loader2 } from 'lucide-react'
import Modal from 'ui/Modal'
import { updateTeamTopIssue } from 'util/teamUtils'
import IssueSearch, { Suggestion } from 'components/Issues/IssueSearch'
import api from 'config/axiosConfig'
import { Issue, Team } from 'util/constants'
import { toast } from 'sonner'
import ActorPrivacy from 'components/Common/ActorPrivacy'
import { createIssueFromWikipedia } from 'util/issueCreationUtils'
import { apiRoutes } from "util/apiRoutes";

interface Props {
  team: Team;
  onClose: () => void;
}

const TeamAboutIssueModal: React.FC<Props> = ({ team, onClose }) => {
  const [selectedIssue, setSelectedIssue] = useState<Suggestion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prepopulatedIssue, setPrepopulatedIssue] = useState<Issue | null>(null);
  const [issueId, setIssueId] = useState<number | null>(team.about?.bio_issue?.value || null);    
  let isButtonDisabled = isSubmitting || (!prepopulatedIssue && !selectedIssue);

  useEffect(() => {
    const fetchExistingIssue = async () => {
      const existingIssueId = team.about?.bio_issue?.value || null;
      if (existingIssueId) {
        try {
          const response = await api.get<Issue>(apiRoutes.issues.byId(existingIssueId));
          setPrepopulatedIssue(response.data);
          setIssueId(existingIssueId);
        } catch (error) {
          console.error('Error fetching existing issue:', error);
          setError('Failed to load existing issue.');
        }
      }
    };

    fetchExistingIssue();
  }, [team.about?.bio_issue?.value]);
  
  const handleIssueSelect = async (suggestion: Suggestion) => {
    setError(null);
    isButtonDisabled = true;

    if (suggestion.type === 'wiki') {
      try {
        const createdIssue = await createIssueFromWikipedia(suggestion);
        setIssueId(createdIssue.id);
        toast.success('New issue created!');
      } catch (error) {
        console.error('Error creating new issue:', error);
        toast.error('Failed to create new issue.');
        setError('Failed to create new issue.');
        return;
      }
    } else {
      setIssueId(suggestion.id);
    }

    setSelectedIssue(suggestion);
    isButtonDisabled = false;
  };

  const handleSubmit = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      await updateTeamTopIssue(team.id, issueId);
      if (team.about?.bio_issue?.value) {
        team.about.bio_issue.value = issueId;
      }
      toast.success('Top issue updated successfully!');
      onClose();
    } catch (error) {
      console.error('Error updating top issue:', error);
      setError('Failed to update top issue. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (): void => {
    onClose();
  };

  const handleEntityChipClose = () => {
    setSelectedIssue(null);
    setIssueId(null);
  };

  isButtonDisabled = isSubmitting || (!prepopulatedIssue && !selectedIssue);

  const footer = (
    <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex sm:flex-row sm:justify-end sm:gap-x-2 py-2">
      <button
        type="button"
        onClick={onClose}
        className="btn-secondary w-full sm:w-fit py-2 px-4"
      >
        Cancel
      </button>
      <button
        type="submit"
        onClick={handleSubmit}
        disabled={isButtonDisabled}
        className={`w-full sm:w-fit py-2 px-4 ${
          isButtonDisabled ? 'btn-primary-disabled' : 'btn-primary'
        } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : 'Submit'}
      </button>
    </div>
  );

  return (
    <Modal isOpen={true} onClose={handleClose} title="#1 Issue" footer={footer} stickyFooter>
      <div className="flex flex-col w-full sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
        <div className="flex flex-col flex-grow space-y-4">
          <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            What's the most pressing issue to your partea? This will be displayed on your partea's profile.
          </p>

          <div className="form-control space-y-4">
            <div className="relative">
              <IssueSearch
                onSelect={handleIssueSelect}
                placeholder="Search for an issue"
                inputType="add"
                fieldRequired={false}
                prepopulateData={prepopulatedIssue}
                onEntityChipClose={handleEntityChipClose}
              />
            </div>

            <div className="relative">
              <ActorPrivacy 
                privacy={'public'}
                settingKey="bio_issue"
                onPrivacyChange={() => {}}
                actorType="team"
                editMode={true}
                showDropdown={false}
                miniIcon={false}
                disabled={true}
                className="opacity-50"
                viewerRelation={team.viewerRelation}
              />
            </div>

            {error && (
              <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
                <Info className="mr-1 size-4" />
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>

        {/* Action buttons moved to Modal footer */}
      </div>
    </Modal>
  );
};

export default TeamAboutIssueModal;
